import React, { type FC, useCallback, useMemo, useState } from "react";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDateValue,
    BusinessDivisionValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import type { StockActInv } from "../../../../api/stockActInvs";
import { StockFullInventoryContentsList } from "./components/StockFullInventoryContentsList/StockFullInventoryContentsList";
import { StockFullInventoryScanPlaceholder } from "./components/StockFullInventoryScanPlaceholder/StockFullInventoryScanPlaceholder";
import { isStockFullInventoryContentLotBased } from "./helpers/formatStockFullInventoryContent";
import { useStockFullInventoryCardQuery, useStockFullInventoryContentsQuery } from "./hooks";
import { stockFullInventoryCardWindows } from "./stockFullInventoryCard.windows";
import { useStockFullInventoryCardMutations } from "./useStockFullInventoryCardMutations";
import "./StockFullInventoryCard.scss";

const STOCK_FULL_INVENTORY_CARD_SCAN_PAUSE_WINDOW_KEYS = [stockFullInventoryCardWindows.content.key];

/** Карточка полной инвентаризации склада по АИ. */
export const StockFullInventoryCard: FC = () => {
    const actCode = useRequiredNumberParam("code", "код АИ");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockFullInventoryCardQuery(actCode);
    const contentsQuery = useStockFullInventoryContentsQuery(actCode);
    const act = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(act?.Code, act != null);
    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(STOCK_FULL_INVENTORY_CARD_SCAN_PAUSE_WINDOW_KEYS);
    /** По ТЗ состав АИ скрыт, пока пользователь не включит отображение. */
    const [showContents, setShowContents] = useState(false);

    const mutations = useStockFullInventoryCardMutations({
        onContentRefetch: (contentCode) => contentsQuery.refetchContentByCode(contentCode),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentAct: StockActInv) => {
        return {
            operation,
            boCode: currentAct.Code,
            documentLabel: `АИ №${currentAct.Num}`,
            fields: [
                { label: "Склад", value: formatEmptyValue(formatDivisionText(currentAct.Division)) },
                { label: "Статус", value: formatEmptyValue(formatBoStateText(currentAct.State)) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: act,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        const sections: ActionSheetSection[] = [
            {
                id: "view",
                items: [
                    {
                        type: "toggle",
                        id: "show-contents",
                        label: "Отображать состав АИ",
                        description: "Показать список строк акта на карточке",
                        checked: showContents,
                        onCheckedChange: setShowContents,
                    },
                ],
            },
        ];

        if (operationItems.length > 0) {
            sections.push({
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            });
        }

        return sections;
    }, [operationItems, showContents]);

    const openContentWindow = useCallback(
        (contentCode: number): void => {
            const content = contentsQuery.content.find((item) => item.Code === contentCode);
            if (content == null || !isStockFullInventoryContentLotBased(content)) {
                return;
            }

            stockFullInventoryCardWindows.content.open(windows, {
                contentCode,
                contents: contentsQuery.content,
                updateFactQtiesMutation: mutations.updateFactQtiesMutation,
            });
        },
        [contentsQuery.content, mutations.updateFactQtiesMutation, windows],
    );

    if (cardQuery.isFetching || act == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_full_inventory_card_page">
            <CardHeader
                text={`Полная инвентаризация по АИ №${act.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList className="stock_full_inventory_card_inform" layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Статус АИ" value={<BusinessBoStateValue state={act.State} />} />
                <DetailList.Row label="Склад" value={<BusinessDivisionValue division={act.Division} />} />
                <DetailList.Row label="Дата" value={<BusinessDateValue value={act.Date} />} />
            </DetailList>
            {showContents ? (
                <StockFullInventoryContentsList contentsQuery={contentsQuery} onOpenContent={openContentWindow} />
            ) : null}
            <StockFullInventoryScanPlaceholder
                contents={contentsQuery.content}
                enabled={!contentsQuery.isFetching && !isCardScannerSuppressedByWindow}
                onOpenContent={openContentWindow}
            />
        </div>
    );
};

function formatEmptyValue(value: string): string {
    return value === "" ? "—" : value;
}
